import { useEffect, useMemo, useState } from 'react';
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { formatDate, getYearOptions } from '@/utility/utility';
import { PopoverContent, Popover, PopoverTrigger } from '@/components/popover';
import { useTranslatedMonths } from '@/hooks';
import styles from './LeaveCalendarHeader.module.css';

interface LeaveCalendarHeaderProps {
  selectedDate?: Date;
  onDateChange?: (date: Date) => void;
  filters?: React.ReactNode;
  /** `YYYY-MM` keys that have leave events. */
  monthsWithLeave?: string[];
}

export function LeaveCalendarHeader({
  filters,
  onDateChange,
  selectedDate = new Date(),
  monthsWithLeave,
}: LeaveCalendarHeaderProps) {
  const monthOptions = useTranslatedMonths();
  const [date, setDate] = useState(selectedDate);
  const yearOptions = useMemo(() => getYearOptions(), []);
  const selectedMonth = date.getMonth();
  const selectedYear = date.getFullYear();
  const { t } = useTranslation('leave-management');

  useEffect(() => onDateChange?.(date), [date, onDateChange]);

  const updateMonthBy = (offset: number) => {
    setDate((prev) => new Date(prev.getFullYear(), prev.getMonth() + offset));
  };

  const canMoveForward =
    selectedYear !== yearOptions[yearOptions.length - 1].value ||
    selectedMonth < monthOptions[monthOptions.length - 1].value - 1;

  const canMoveBackward =
    selectedYear !== yearOptions[0].value ||
    selectedMonth > monthOptions[0].value - 1;

  const today = new Date();
  const isCurrentMonth =
    selectedYear === today.getFullYear() && selectedMonth === today.getMonth();

  const goToToday = () =>
    setDate(new Date(today.getFullYear(), today.getMonth()));

  return (
    <div className={styles.toolbar}>
      <div className={styles.navGroup}>
        <button
          type="button"
          className={styles.navBtn}
          disabled={!canMoveBackward}
          onClick={() => updateMonthBy(-1)}
          aria-label={t('calendar.previous_month')}
        >
          <ChevronLeftIcon aria-hidden="true" />
        </button>
        <MonthYearPicker
          selectedDate={date}
          onDateChange={setDate}
          monthOptions={monthOptions}
          yearOptions={yearOptions}
          monthsWithLeave={monthsWithLeave}
        />
        <button
          type="button"
          className={styles.navBtn}
          disabled={!canMoveForward}
          onClick={() => updateMonthBy(1)}
          aria-label={t('calendar.next_month')}
        >
          <ChevronRightIcon aria-hidden="true" />
        </button>
        <button
          type="button"
          className={styles.todayBtn}
          onClick={goToToday}
          disabled={isCurrentMonth}
        >
          {t('calendar.today')}
        </button>
      </div>

      {filters && <div className={styles.filters}>{filters}</div>}
    </div>
  );
}

interface MonthYearPickerProps {
  selectedDate: Date;
  onDateChange: (date: Date) => void;
  monthOptions: ReturnType<typeof useTranslatedMonths>;
  yearOptions: ReturnType<typeof getYearOptions>;
  monthsWithLeave?: string[];
}

function MonthYearPicker({
  selectedDate,
  onDateChange,
  monthOptions,
  yearOptions,
  monthsWithLeave,
}: MonthYearPickerProps) {
  const { i18n, t } = useTranslation('leave-management');
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState(selectedDate.getMonth());
  const [year, setYear] = useState(selectedDate.getFullYear());

  const today = new Date();

  useEffect(() => {
    setMonth(selectedDate.getMonth());
    setYear(selectedDate.getFullYear());
  }, [selectedDate]);

  const minYear = yearOptions[0].value;
  const maxYear = yearOptions[yearOptions.length - 1].value;

  const selectMonth = (m: number) => {
    const newDate = new Date(year, m);
    setMonth(m);
    onDateChange(newDate);
    setOpen(false);
  };

  const goToThisMonth = () => {
    onDateChange(new Date(today.getFullYear(), today.getMonth()));
    setOpen(false);
  };

  const leaveSet = useMemo(
    () => new Set(monthsWithLeave ?? []),
    [monthsWithLeave]
  );

  const triggerDate = useMemo(
    () => new Date(selectedDate.getFullYear(), selectedDate.getMonth()),
    [selectedDate]
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={styles.titleBtn}
          aria-haspopup="dialog"
        >
          <span className={styles.titleLabel}>
            {formatDate(triggerDate, {
              format: 'MMMM YYYY',
              locale: i18n.language,
            })}
          </span>
          <ChevronDownIcon
            aria-hidden="true"
            className={cn(styles.titleChevron, open && styles.titleChevronOpen)}
          />
        </button>
      </PopoverTrigger>

      <PopoverContent
        className={cn('p-0 shadow-lg border-border', styles.panel)}
        sideOffset={8}
        align="start"
      >
        {/* Year navigation */}
        <div className={styles.yearNav}>
          <button
            type="button"
            className={styles.yearBtn}
            aria-label={t('calendar.previous_year')}
            disabled={year <= minYear}
            onClick={() => setYear((y) => y - 1)}
          >
            <ChevronLeftIcon />
          </button>
          <span className={styles.yearLabel}>{year}</span>
          <button
            type="button"
            className={styles.yearBtn}
            aria-label={t('calendar.next_year')}
            disabled={year >= maxYear}
            onClick={() => setYear((y) => y + 1)}
          >
            <ChevronRightIcon />
          </button>
        </div>

        {/* Month grid */}
        <div className={styles.monthGrid}>
          {monthOptions.map(({ label, value }) => {
            const m = value - 1;
            const isSelected =
              m === month && year === selectedDate.getFullYear();
            const isCurrent =
              m === today.getMonth() && year === today.getFullYear();
            const hasLeave = leaveSet.has(
              `${year}-${String(value).padStart(2, '0')}`
            );

            return (
              <button
                key={value}
                type="button"
                className={cn(
                  styles.monthBtn,
                  isCurrent && !isSelected && styles.monthBtnCurrent,
                  isSelected && styles.monthBtnSelected
                )}
                aria-pressed={isSelected}
                aria-current={isCurrent ? 'date' : undefined}
                onClick={() => selectMonth(m)}
              >
                {label}
                <span
                  className={cn(
                    styles.monthDot,
                    hasLeave && styles.monthDotLeave
                  )}
                />
              </button>
            );
          })}
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <span className={styles.legend}>
            <span className={styles.legendDot} aria-hidden="true" />
            {t('calendar.has_leave')}
          </span>
          <button
            type="button"
            className={styles.thisMonthBtn}
            onClick={goToThisMonth}
          >
            {t('calendar.this_month')}
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
